import React, { useCallback, useEffect, useRef } from "react";
import { Card, Slider, Computed, Tile } from "./shared";
import * as m from "../lib/model";

const METHODS = [
  { id: "days", label: "Working-capital cycle (days)" },
  { id: "percent", label: "% of revenue" },
  { id: "manual", label: "Manual $" },
];

// Working-capital inputs + result. Lives in the Assumptions rail so every deal input sits in one place.
export function WorkingCapitalPanel({ M, setField }) {
  const wc = m.workingCapital(M);

  // Feed the computed requirement back into the model so it cascades into Uses, goodwill and Sources & Uses.
  useEffect(() => {
    if (M.wcMethod === "manual") return;
    const funded = m.workingCapital(M).funded;
    if (funded !== M.nwc) setField("nwc", funded);
  }, [M, setField]);

  const mRef = useRef(M);
  mRef.current = M;
  const S = useCallback((props) => (
    <Slider {...props} value={mRef.current[props.k]} onChange={(v) => setField(props.k, v)} />
  ), [setField]);

  const method = M.wcMethod || "days";
  const goodwill = Math.max(0, M.askPrice - (M.ffe || 0) - (M.nwc || 0));

  return (
    <div data-testid="wc-panel">
      <Card title="Calculation method" testid="wc-method" style={{ marginBottom: 12 }}>
        <div className="scen-bar" data-testid="wc-method-bar" style={{ maxWidth: 560 }}>
          {METHODS.map((x) => (
            <button key={x.id} className={method === x.id ? "active" : ""} onClick={() => setField("wcMethod", x.id)} data-testid={`wc-method-${x.id}`}>
              {x.label}
            </button>
          ))}
        </div>
      </Card>

      <div className="grid g2" style={{ alignItems: "start" }}>
        <div>
          <Card title="1 · Business inputs" testid="wc-business">
            <S label="TTM revenue" k="ttmRevenue" min={0} max={50000000} step={50000} format={m.fmt$} testid="wc-ttmRevenue" />
            <S label="Gross margin %" k="grossMarginPct" min={0} max={90} step={0.5} format={(v) => `${v}%`} testid="wc-grossMargin" />
            <Computed label="Implied annual COGS" value={m.fmt$(wc.cogs)} />
          </Card>

          <Card title="2 · Working-capital drivers" style={{ marginTop: 12 }} testid="wc-drivers">
            {method === "days" && (
              <>
                <S label="A/R — days sales outstanding (DSO)" k="dso" min={0} max={120} step={1} format={(v) => `${v} d`} testid="wc-dso" />
                <S label="Inventory — days on hand (DIO)" k="dio" min={0} max={180} step={1} format={(v) => `${v} d`} testid="wc-dio" />
                <S label="A/P — days payable (DPO)" k="dpo" min={0} max={120} step={1} format={(v) => `${v} d`} testid="wc-dpo" />
                <Computed label="Cash-conversion cycle" value={`${wc.cycleDays} days`} tone={wc.cycleDays <= 30 ? "go" : wc.cycleDays >= 75 ? "pass" : undefined} />
              </>
            )}
            {method === "percent" && (
              <S label="NWC as % of TTM revenue" k="wcPctRevenue" min={0} max={30} step={0.5} format={(v) => `${v}%`} testid="wc-pctRevenue" />
            )}
            {method === "manual" && (
              <S label="Net working capital ($)" k="nwc" min={0} max={1000000} step={2500} format={m.fmt$} testid="wc-manual" />
            )}
            {method !== "manual" && (
              <S label="% of target funded in deal" k="nwcFundedPct" min={0} max={100} step={5} format={(v) => `${v}%`} testid="wc-fundedPct" />
            )}
            <div className="bullet" style={{ marginTop: 10 }}>
              {method === "days" && "Net WC = A/R + Inventory − A/P, sized off TTM revenue & COGS. "}
              {method === "percent" && "Net WC = the % applied to TTM revenue. "}
              {method === "manual" && "Enter the working capital dollar amount directly. "}
              {method !== "manual" && "The seller typically delivers a normalized peg at close — fund only the portion your deal carries in Uses."}
            </div>
          </Card>
        </div>

        <div>
          <Card title="Computed working capital" testid="wc-result">
            <div className="grid g3" style={{ marginBottom: 12 }}>
              <Tile label="Net WC target" value={m.fmt$(wc.netWC)} testid="wc-tile-target" />
              <Tile label="Funded in deal" value={m.fmt$(wc.funded)} tone="go" testid="wc-tile-funded" />
              <Tile label="Cash cycle" value={`${wc.cycleDays} d`} testid="wc-tile-cycle" />
            </div>
            <table className="tbl" data-testid="wc-breakdown">
              <thead><tr><th>Component</th><th className="num">Days</th><th className="num">Amount</th></tr></thead>
              <tbody>
                <tr><td>Accounts receivable (A/R)</td><td className="num">{method === "days" ? M.dso : "—"}</td><td className="num">{m.fmt$(wc.ar)}</td></tr>
                <tr><td>Inventory</td><td className="num">{method === "days" ? M.dio : "—"}</td><td className="num">{m.fmt$(wc.inventory)}</td></tr>
                <tr><td>Less: accounts payable (A/P)</td><td className="num">{method === "days" ? M.dpo : "—"}</td><td className="num">({m.fmt$(wc.ap)})</td></tr>
                <tr className="total"><td>Net working capital</td><td className="num" /><td className="num">{m.fmt$(wc.netWC)}</td></tr>
                <tr><td>Delivered by seller at close</td><td className="num" /><td className="num">{m.fmt$(wc.netWC - wc.funded)}</td></tr>
                <tr className="total"><td>Buyer-funded (into Uses)</td><td className="num" /><td className="num">{m.fmt$(wc.funded)}</td></tr>
              </tbody>
            </table>
          </Card>

          <AddOnWorkingCapital M={M} />

          <Card title="Flows into your deal" style={{ marginTop: 12 }} testid="wc-flow">
            <Computed label="→ Working capital in Uses (NWC)" value={m.fmt$(M.nwc || 0)} />
            {(() => { const rows = m.addonTableRows(M); const sf = rows.find((r) => r.selfFunded); const com = rows.filter((r) => !r.selfFunded).reduce((a, r) => a + (r.nwc || 0), 0);
              return rows.length ? <Computed label={`→ Consolidated working capital (platform + ${rows.filter((r) => !r.selfFunded).length} committed${sf ? ` + ${sf.count} self-funded` : ""} add-ons)`} value={m.fmt$((M.nwc || 0) + com + (sf ? sf.nwc : 0))} tone="go" testid="wc-consolidated" /> : null; })()}
            <Computed label="→ Implied goodwill (price − FF&E − NWC)" value={m.fmt$(goodwill)} />
            <Computed label="→ Net acquisition cost" value={m.fmt$(m.nac(M))} />
            <div className="bullet" style={{ marginTop: 8 }}>
              This buyer-funded figure updates the <b>NWC / inventory</b> line in the Dashboard <b>Sources &amp; Uses</b>, the goodwill allocation, and the exported firm Excel model automatically.
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// The same WC method applied to each add-on's implied revenue, funded in cash at each close.
function AddOnWorkingCapital({ M }) {
  const rows = m.addonTableRows(M);
  const ratio = M.sde > 0 ? (M.ttmRevenue || 0) / M.sde : 0;
  const method = M.wcMethod || "days";
  if (!rows.length) return null;
  return (
    <Card title="Add-on working capital — funded at each close" style={{ marginTop: 12 }} testid="wc-addons">
      <table className="tbl tbl-tight" data-testid="wc-addon-table">
        <thead><tr><th>Add-on</th><th className="num">Closes</th><th className="num">EBITDA</th><th className="num">Implied revenue</th><th className="num">NWC funded</th></tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} data-testid={`wc-addon-row-${i}`}>
              <td>{r.name}</td><td className="num mono">{r.yearLabel || `Yr ${r.year}`}</td><td className="num">{m.fmt$(r.ebitda)}</td>
              <td className="num">{m.fmt$(r.ebitda * ratio)}</td><td className="num" style={{ fontWeight: 600 }}>{m.fmt$(r.nwc)}</td>
            </tr>
          ))}
          <tr className="total"><td>All add-ons</td><td className="num">—</td><td className="num">{m.fmt$(rows.reduce((a, r) => a + r.ebitda, 0))}</td>
            <td className="num">{m.fmt$(rows.reduce((a, r) => a + r.ebitda * ratio, 0))}</td><td className="num">{m.fmt$(rows.reduce((a, r) => a + r.nwc, 0))}</td></tr>
        </tbody>
      </table>
      <div className="ir-fine" style={{ marginTop: 6 }}>
        Each add-on's revenue is implied from its EBITDA at the platform's margin, then your{" "}
        {method === "days" ? "cash-cycle (DSO/DIO/DPO)" : method === "percent" ? "% of revenue" : "manual NWC as a % of revenue"} method sizes the
        requirement. It is funded in cash at close inside the add-on equity check, so it flows into the consolidated Sources &amp; Uses and the IRR.
      </div>
    </Card>
  );
}
